import { DataFormatter } from "../dataformats/DataFormatter";
import { LogicEngine } from "../LogicEngine/LogicEngine";
import { PageStepType } from "../PageParser/PageTypes";
import { DataDictionaryTableRawType } from "../types/DataDictionaryType";
import { DataDictionary, DataDictionaryTable } from "./DataDictionary";
import { DataQuery } from "./DataQuery";
import { DataRecord } from "./DataRecord";
import { DataServices } from "./DataServices";
import { DataTools } from "./DataTools";
import { StringOptions } from "./StringOptions";

/**

Wrapper class that goes around a set of Data Records.
This replaces DataRecord[] with a class to manage the records
and associate them with the Data Dictionary.

*/

export class DataSet
{
	// The records within this dataset
	_records: { [key: string]: DataRecord[] } = {};

	// A backup copy of the records for prcoessing.
	_recordsRaw: DataRecord[] = [];

	// The data dictionary table that represents the columns within the dataset
	tableDef: DataDictionaryTable | undefined;

	// Reference to a logic engine for grouping and other actions
	logicRef: LogicEngine | undefined;

	// Handle potential group by command
	actions: DataSet.DataSetOption[] = [];

	// Default data set name for use in logic engine.
	setName: string = "table";

	options: StringOptions = new StringOptions("");

	DataSet()
	{
	}

	/** Set the options that are used the process the dataset */
	setOptions(strValue: string | undefined)
	{
		if (strValue === undefined)
		{
			this.options = new StringOptions("");
			return;
		}

		this.options = new StringOptions(DataTools.internalValidateString(strValue));

		if (LogicEngine.debugOutput)
		{
			console.log("DataSet setOptions:", this.options.value);
		}
	}

	/**
	 * Parse the options and see if they apply to the dataset
	 * @param step The PageStep with the configuration for options in "step_options".
	 */
	checkDatasetOption(step: PageStepType)
	{
		let key = step.step_option ? step.step_option : "";
		if (key === "") return false;

		let option = new StringOptions(key);
		if (option.checkOption("FilterUnique"))
		{
			this.addAction({
				type: 'FilterUnique',
				condition: step.text_en || "",
				targetFormat: step.option_2 || "Text",
				targetVariable: step.field_id || "unique_id"
			});

			return true;
		}

		if (option.checkOption("CountWhere"))
		{
			this.addAction({
				type: 'CountWhere',
				condition: step.text_en || "",
				targetFormat: step.option_2 || "",
				targetVariable: step.field_id || "",
				targetAmount: step.option_1 || ""
			});

			return true;
		}

		if (option.checkOption("SkipWhere"))
		{
			this.addAction({
				type: 'SkipWhere',
				condition: step.text_en || "",
				targetFormat: step.option_2 || "",
				targetVariable: step.field_id || "",
				targetAmount: step.option_1 || ""
			});

			return true;
		}

		if (option.checkOption("RowColor"))
		{
			this.addAction({
				type: 'RowColor',
				condition: step.text_en || "",
				targetFormat: step.option_2 || "",
				targetVariable: step.field_id || "",
				targetAmount: step.option_1 || option.checkOption("RowColor")
			});

			return true;
		}

		if (option.checkOption("ColumnColor"))
		{
			this.addAction({
				type: 'ColumnColor',
				condition: step.text_en || "",
				targetFormat: step.option_2 || "",
				targetVariable: step.field_id || "",
				targetAmount: step.option_1 || option.checkOption("ColumnColor")
			});

			return true;
		}

		return false;
	}

	/*
	Used when you have a table with a groupby query
	Type=Begin Table
	Step Option=QueryGroup
	Text= The query to execute
	Field= One or more fields to group by
	Output = The resulting output adds "count" to the output
	**/
	async handleQueryGroupby(strText: string, strFields: string): Promise<{ [key: string]: number }>
	{
		let queryResultRecords = await DataQuery.instance().executeQueryByText(strText);
		const groupCounter: { [key: string]: number } = {};

		// Set a list of column names
		const columnName: string[] = strFields.split(',').map(a => a.trim().toLowerCase());

		/* Traverse the Data records */
		queryResultRecords.forEach((record) =>
		{
			if (record.data)
			{
				const values: { [key: string]: any } = {};

				for (let field in record.data)
				{
					if (columnName.includes(field))
					{
						values[field] = record.data[field];
					}
				}

				const keyString: string = JSON.stringify(values);

				if (keyString in groupCounter)
					groupCounter[keyString]++;
				else
					groupCounter[keyString] = 1;
			}
		});

		return groupCounter;
	}

	/** Generate a dataset from Group By query.  See handleQuerygroupBy */
	async generateDatasetFromGroupBy(strText: string, strFields: string)
	{
		const groupCounter: { [key: string]: number } = await this.handleQueryGroupby(strText, strFields);

		// Add couter records to the table
		for (let key in groupCounter)
		{
			const values = JSON.parse(key);

			const dataRecord = new DataRecord('Counter', {
				count: groupCounter[key],
				...values,
			});

			this.add(dataRecord);
		}

		// Set tableDef
		this.tableDef = this.getDataDictionaryTableForCounter();
	}


	/** Generate a dataset where the query is used to get data from the database
	using the API on the server.
	Begin Table Option=Query Text=The query to execute
	*/
	async generateDatsetFromQuery(strText: string)
	{
		if (!this.logicRef) throw "Missing or invalid logicRef in generateDatasetFromList";

		const queryText = this.logicRef?.processTextReplacement(strText);
		await this.setFromQuery(queryText);
	}

	/** Geerate a dataset from a list of items.
	The resultsing dataset will have counter and id columns
	where counter is a number from 1 to the end.
	id is the text value from a list.
	Begin Table Option=List Text=comma or ;; seperated list.
	@example text=Cat;;Ball;;Dog
	@example text=Bill, Mike, Fred, Orange
	*/
	generateDatasetFromList(strText: string)
	{
		if (!this.logicRef) throw "Missing or invalid logicRef in generateDatasetFromList";

		// Create a record based on start and end value
		let text = this.logicRef.processTextReplacement(strText);

		// split with , by default or ;; if found
		let listItems = text.split(",");
		if (text.indexOf(";;") != -1) listItems = text.split(";;");

		if (LogicEngine.debugOutput)
			console.log("DataSet generateDatasetFromList List=", listItems);

		// Add couter records to the table
		let counter = 0;
		for (let i of listItems)
		{
			const dataRecord = new DataRecord('Counter', {
				id: i.trim(),
				counter: counter,
			});

			counter++;
			this.add(dataRecord);

			// Exit if needed before infinite loop
			if (counter > 10000)
				throw "List too long in generateDatasetFromList";
		}

		// Set tableDef
		this.tableDef = this.getDataDictionaryTableForCounter();
	}

	/** Create a fake dataset as a counter, like a for loop
	Where you have "Begin Table" and option="Counter"
	Optionally you can set start or end in text column as
	@example start=5
	@example start=1;end=10
	@example end=50
	@example { start: 10, end: 50 }
	@example { start: 1, end: 100, skip: 2 }
	*/
	generateDatasetFromCounter(strText: string)
	{
		if (this.logicRef)
			strText = this.logicRef?.processTextReplacement(strText);

		let { start, end, skip } = DataTools.getOptions(strText);
		if (LogicEngine.debugOutput)
			console.log("DataSet generateDatasetFromCounter Options:", strText, " start=", start, "end=", end, "skip=", skip);

		// Make sure we have number values here
		skip = DataTools.getNumber(skip);
		start = DataTools.getNumber(start);
		end = DataTools.getNumber(end);

		// Default value for start is 0
		if (!start)
			start = 0;

		// Ending of the loop inclusive of this value
		if (!end)
			end = start;

		// how much to increment
		if (!skip)
			skip = 1;

		// Track the attempts in the loop incase there is an issue
		// such as numbers that never get reacherd.
		let attempts = 0;

		// Add couter records to the table
		for (let i = start; i <= end; i = i + skip)
		{
			if (++attempts > 10000)
				throw "Exception in StepTable generateDatasetFromCounter, maximum loop size exceeded.";

			const dataRecord = new DataRecord('Counter', {
				id: i,
				counter: i,
			});

			this.add(dataRecord);
		}

		// Set tableDef
		this.tableDef = this.getDataDictionaryTableForCounter();
	}

	/** Add an action to the list of things to be done to the data before formatting. */
	addAction(action: DataSet.DataSetOption)
	{
		for (const old of this.actions)
		{
			if (action.type == old.type)
			{
				if (action.condition == old.condition)
				{
					old.targetVariable = action.targetVariable;
					old.targetAmount = action.targetAmount;
					old.targetFormat = action.targetFormat;
					return;
				}
			}
		}

		this.actions.push(action);
	}

	/* Add a record to the dataset */
	add(r: DataRecord)
	{
		// Check to see if we have a "prefix" option
		if (this.options.checkOption("prefix"))
		{
			let prefixTemplate = DataTools.internalValidateString(this.options.checkOption("prefix"));

			// Default prefix to the name of the table if not specified
			if ((prefixTemplate === "" || prefixTemplate === "Yes") && this.tableDef && this.tableDef.tableName)
				prefixTemplate = this.tableDef.tableName;



			//  Backup old values from logic engine
			let oldValues: any = {};
			for (const key of Object.keys(r.data))
			{
				oldValues[key] = this.logicRef?.getVariable(key);
			}

			// Import record for processing the prefix name
			this.logicRef?.import(r.data);

			// Rename all elements of the data row
			for (const key of Object.keys(r.data))
			{
				let newKey = prefixTemplate + "_" + key;
				if (this.logicRef)
					newKey = this.logicRef.fnIndexName(this.logicRef.processTextReplacement(prefixTemplate), key);

				r.data[newKey] = r.data[key];
				delete r.data[key];
			}

			// Restore values
			this.logicRef?.import(oldValues);
		}

		/** Only allow a select set of fields to be approved */
		if (this.options.checkOption("fields"))
		{
			const allowList: string[] = [];
			const fieldList = DataTools.internalValidateString(this.options.checkOption("fields")).split(",");
			for (let fieldName of fieldList)
			{
				fieldName = fieldName.trim();
				allowList.push(fieldName);
			}

			for (const key of Object.keys(r.data))
			{
				if (allowList.indexOf(key) == -1)
					delete r.data[key];
			}
		}

		this._recordsRaw.push(r);
	}

	/** Process the array of records into an index where the index
	is either a sequential number by default or a unique key
	*/
	processRawRecords()
	{
		this._records = {};

		for (const r of this._recordsRaw)
		{
			// by default, no filter so the row_id is a sequence number
			// starting at 0
			let row_id: string = Object.keys(this._records).length + '';

			for (const action of this.actions)
			{
				if (action.type === 'FilterUnique')
				{
					row_id = this.processActionFilterUnique(action, r);
				}

				if (action.type == 'SkipWhere')
				{
					if (this.processActionSkipWhere(action, r))
						row_id = "SKIP";
				}
			}

			//  Each dictionary in _records is an array of one result by default
			//  but that would change if filterunique returns a key that matches

			if (row_id === "SKIP")
				continue;

			if (!this._records[row_id])
				this._records[row_id] = [];

			this._records[row_id].push(r);
		}
	}

	processActionSkipWhere(action: DataSet.DataSetOption, dr: DataRecord)
	{
		if (!this.logicRef) return '';
		this.logicRef.import(dr.data);
		const key = this.logicRef.processTextReplacement(action.condition);
		const result = this.logicRef.process(key);
		let df = new DataFormatter();
		const logicalResult = df.unformatData('boolean', result);
		console.log("SKIP", dr.data, "=", logicalResult);
		return logicalResult;
	}

	processActionFilterUnique(action: DataSet.DataSetOption, dr: DataRecord)
	{
		if (!this.logicRef) return '';
		this.logicRef.import(dr.data);
		const key = this.logicRef.processTextReplacement(action.condition);
		return key;
	}

	/* Count or perform a function on the record in drList */
	processActionCountWhere(action: DataSet.DataSetOption, drList: DataRecord[], newData: DataRecord)
	{
		if (!this.logicRef) return '';

		for (const dr of drList)
		{
			this.logicRef.import(dr.data);
			const amountText = this.logicRef.processTextReplacement(action.targetAmount);
			let amount = parseInt(amountText, 10);
			if (isNaN(amount)) amount = 1;

			const condition = this.logicRef.processTextReplacement(action.condition);
			const result = this.logicRef.process(condition);
			let df = new DataFormatter();
			const logicalResult = df.unformatData('boolean', result);
			if (logicalResult)
			{
				if (LogicEngine.debugOutput)
					console.log("DataSet Match ", dr.data, " for ", condition);

				/// CountWhere is a "Sum" action
				if (action.type === 'CountWhere')
				{
					if (!newData.data[action.targetVariable])
						newData.data[action.targetVariable] = 0;

					newData.data[action.targetVariable] += amount;
				}
			}
		}
	}

	/* Reference to the records we should use for this dataset */
	getRecords(): DataRecord[]
	{
		//  No actions defined so quickly return the set of records.
		// Reset the list before running actions
		const results: DataRecord[] = [];

		this.processRawRecords();
		for (const row_id of Object.keys(this._records))
		{
			const first = this._records[row_id][0];
			const newRecord: DataRecord = new DataRecord(first.tableName, first.data);
			newRecord.data.row_id = row_id;

			for (const action of this.actions)
			{
				if (action.type === 'CountWhere')
				{
					this.processActionCountWhere(action, this._records[row_id], newRecord);
				}
			}

			results.push(newRecord);
		}

		return results;
	}

	/** Given a valid query, run the query against the server and populate the
	datarecords in this set and load the data dictionary from the table.
	*/
	async setFromQuery(queryText: string)
	{
		// Parse the name of the table from the standard loris query syntax
		const tableName = queryText.split(';').shift();

		if (!tableName || tableName === '')
		{
			console.error("DataSet setFromQuery missing table name in:" + queryText);
			return;
		}

		/* Load the data dictionary information for this table */
		const dataService = DataServices.instance();
		const dd: DataDictionary = await dataService.getDataDictionary();
		this.tableDef = dd.getTable(tableName + '');

		const results = await DataQuery.instance().executeQueryByText(queryText);
		for (const result of results)
		{
			this.add(result);
		}
	}

	/**
	 * Generate the object required for a DataDictionary Table Definition based on a
	 * table called "Counter" for internal counting (for loop) instead of a data
	 * source from a database.
	 * @returns Data Dictionary Definition for a table named "Counter" with id and counter columns
	 */
	getDataDictionaryTableForCounter = (): DataDictionaryTable =>
	{
		const dataDictionaryTableType: DataDictionaryTableRawType[] = [
			{
				tableName: 'Counter',
				data: [
					{
						field: 'id',
						field_label_en: 'Counter Index',
						field_type: 'ID',
						validation_name: 'NotEmpty',
						options: '',
						column_header: 'id',
						display_level: '',
						detail_group: '',
						export_name: '',
					},
					{
						field: 'counter',
						field_label_en: 'Counter value',
						field_type: 'TextCounterValue',
						validation_name: 'NotEmpty',
						options: '',
						column_header: 'counter',
						display_level: '',
						detail_group: '',
						export_name: '',
					},
				],
			},
		];

		return new DataDictionaryTable('Counter', dataDictionaryTableType);
	};

	actionCountWhere(action: DataSet.DataSetOption)
	{

	}

}

export namespace DataSet
{
	export interface DataSetOption
	{
		type: 'FilterUnique' | 'CountWhere' | 'SkipWhere' | 'RowColor' | 'ColumnColor'
		condition: string,
		targetVariable: string,
		targetFormat: string
		targetAmount?: string
	};
}
